const apiUrl = process.env.REACT_APP_API_URL;

export interface UserDbMeta {
    name: string;
    schema: string;
    createdAt?: string;
    sizeMb?: number;
    isSystem?: boolean;
    isOwn?: boolean;
    isStudentDb?: boolean;
    studentName?: string;
    studentId?: number;
    aulaName?: string;
    dbName?: string;
    readOnly?: boolean;
}

export interface UserDbColumnMeta {
    name: string;
    type: string;
    pk: boolean;
    fk: boolean;
}

export interface UserDbTableMeta {
    name: string;
    columns: UserDbColumnMeta[];
}

export interface UserDbRoutineMeta {
    name: string;
    returnType?: string;
}

export interface UserDbTriggerMeta {
    name: string;
    event?: string;
    table?: string;
    timing?: string;
}

export interface UserDbSchemaDetail {
    name: string;
    schema: string;
    tables: UserDbTableMeta[];
    views?: UserDbTableMeta[];
    procedures?: UserDbRoutineMeta[];
    functions?: UserDbRoutineMeta[];
    triggers?: UserDbTriggerMeta[];
}

export interface SqlExecuteResultItem {
    sql: string;
    type: 'resultset' | 'update' | 'error';
    columns?: string[];
    rows?: any[][];
    rowCount?: number;
    truncated?: boolean;
    updateCount?: number;
    error?: string;
    errorCode?: number;
    durationMs?: number;
}

export interface SqlExecuteResponse {
    database: string;
    results: SqlExecuteResultItem[];
}

export interface UserSqlFileItem {
    name: string;
    path: string;
    isDir?: boolean;
}

export const SqlIdeApi = {
    // ---- Bases de datos propias del alumno ----
    async listDatabases(): Promise<UserDbMeta[]> {
        const res = await fetch(`${apiUrl}:8061/bbdd/ide/databases`);
        if (!res.ok) throw new Error(await res.text());
        return await res.json();
    },

    async createDatabase(name: string): Promise<{ name: string; schema: string }> {
        const res = await fetch(`${apiUrl}:8061/bbdd/ide/databases`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name })
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Error creant base de dades' }));
            throw new Error(err.error || 'Error creant base de dades');
        }
        return await res.json();
    },

    async dropDatabase(name: string): Promise<void> {
        const res = await fetch(`${apiUrl}:8061/bbdd/ide/databases/${encodeURIComponent(name)}`, {
            method: 'DELETE'
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Error eliminant base de dades' }));
            throw new Error(err.error || 'Error eliminant base de dades');
        }
    },

    async describeDatabase(name: string): Promise<UserDbSchemaDetail> {
        const res = await fetch(`${apiUrl}:8061/bbdd/ide/databases/${encodeURIComponent(name)}/schema`);
        if (!res.ok) throw new Error(await res.text());
        return await res.json();
    },

    // ---- Ejecución real aislada contra la base de datos del alumno ----
    async executeSql(database: string, sql: string, maxRows?: number): Promise<SqlExecuteResponse> {
        const res = await fetch(`${apiUrl}:8061/bbdd/ide/execute`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ database, sql, maxRows })
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Error executant SQL' }));
            throw new Error(err.error || 'Error executant SQL');
        }
        return await res.json();
    },

    // ---- Ficheros .sql persistentes en el NFS/EFS del alumno ----
    async listFiles(targetStudentId?: number): Promise<UserSqlFileItem[]> {
        const url = targetStudentId
            ? `${apiUrl}:8061/bbdd/ide/files?targetStudentId=${targetStudentId}`
            : `${apiUrl}:8061/bbdd/ide/files`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(await res.text());
        return await res.json();
    },

    async createFolder(path: string): Promise<void> {
        const res = await fetch(`${apiUrl}:8061/bbdd/ide/folders`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ path })
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Error creant carpeta' }));
            throw new Error(err.error || 'Error creant carpeta');
        }
    },

    async readFile(name: string, targetStudentId?: number): Promise<string> {
        const url = targetStudentId
            ? `${apiUrl}:8061/bbdd/ide/files/${encodeURIComponent(name)}?targetStudentId=${targetStudentId}`
            : `${apiUrl}:8061/bbdd/ide/files/${encodeURIComponent(name)}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        return data.content ?? '';
    },

    async saveFile(name: string, content: string): Promise<void> {
        const res = await fetch(`${apiUrl}:8061/bbdd/ide/files/${encodeURIComponent(name)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ content })
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Error desant fitxer' }));
            throw new Error(err.error || 'Error desant fitxer');
        }
    },

    async renameFile(oldName: string, newName: string): Promise<void> {
        const res = await fetch(`${apiUrl}:8061/bbdd/ide/files/${encodeURIComponent(oldName)}/rename`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ newName })
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Error reanomenant fitxer' }));
            throw new Error(err.error || 'Error reanomenant fitxer');
        }
    },

    async deleteFile(name: string): Promise<void> {
        const res = await fetch(`${apiUrl}:8061/bbdd/ide/files/${encodeURIComponent(name)}`, {
            method: 'DELETE'
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Error eliminant fitxer' }));
            throw new Error(err.error || 'Error eliminant fitxer');
        }
    }
};
